import React, { useState, useEffect } from 'react';
import { List, Card, Button, Tag, Space, Empty, Spin, message, Modal, Divider, Row, Col, Select, InputNumber, Form, Input } from 'antd';
import { CheckOutlined, EyeOutlined, PlusOutlined, MinusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import pointsTemplatesAPI from '../../services/pointsTemplatesAPI';
import api from '../../services/api';
import PointsItemSelector from '../PointsItemSelector';

const { Option } = Select;

const SystemTemplates = ({ onSelectTemplate }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectorVisible, setSelectorVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [customForm] = Form.useForm();
  const [editingPoints, setEditingPoints] = useState({}); // 记录正在编辑的项目分值
  const [sortBy, setSortBy] = useState('default'); // 排序方式: default, name, points
  const [selectedCategory, setSelectedCategory] = useState(null); // 当前选中的分类
  const [categories, setCategories] = useState([]); // 可用分类列表

  useEffect(() => {
    fetchSystemTemplates();
    fetchClasses();
  }, []);

  const fetchSystemTemplates = async () => {
    try {
      setLoading(true);
      const response = await pointsTemplatesAPI.getSystemTemplates();
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('获取系统模板失败:', error);
      message.error('获取系统模板失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
    }
  };

  // 查看模板详情
  const handleViewDetail = async (template) => {
    try {
      setLoading(true);
      const response = await pointsTemplatesAPI.getSystemTemplateDetail(template.id);
      if (response.data && response.data.success) {
        const detailData = response.data.data || {};
        const detailItems = Array.isArray(detailData.items) ? detailData.items : [];
        
        setCurrentTemplate(template);
        setTemplateDetail(detailData);
        setSelectedItems(detailItems.map(item => item.id));
        setIsEditing(false);
        
        // 提取分类列表
        const categorySet = new Set();
        detailItems.forEach(item => {
          if (item.category) {
            categorySet.add(item.category);
          }
        });
        const categoryList = Array.from(categorySet).sort();
        setCategories(categoryList);
        
        // 默认选中第一个分类
        if (categoryList.length > 0) {
          setSelectedCategory(categoryList[0]);
        }
        
        setDetailVisible(true);
      }
    } catch (error) {
      console.error('获取模板详情失败:', error);
      message.error('获取模板详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 从项目池添加项目
  const handleAddFromPool = (item) => {
    const newItem = {
      id: item.id,
      item_name: item.name,
      item_type: item.type,
      category: item.category,
      default_points: Math.abs(item.points),
      isNew: true
    };
    setTemplateDetail(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setSelectedItems(prev => [...prev, item.id]);
  };

  // 移除项目
  const handleRemoveItem = (itemId) => {
    setTemplateDetail(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    setSelectedItems(prev => prev.filter(id => id !== itemId));
  };

  // 修改项目分值
  const handleChangePoints = (itemId, newPoints) => {
    setTemplateDetail(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, default_points: newPoints, isModified: true } : item
      )
    }));
  };

  // 添加自定义项目
  const handleAddCustomItem = () => {
    customForm.resetFields();
    setCustomModalVisible(true);
  };

  // 保存自定义项目
  const handleSaveCustomItem = async (values) => {
    const newItem = {
      id: `custom_${Date.now()}`,
      item_name: values.item_name,
      item_type: values.item_type,
      category: values.category,
      default_points: values.default_points,
      isNew: true,
      isCustom: true
    };
    
    setTemplateDetail(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setSelectedItems(prev => [...prev, newItem.id]);
    setCustomModalVisible(false);
    message.success('自定义项目已添加');
  };

  // 使用模板
  const handleApplyTemplate = async () => {
    if (!selectedClassId) {
      message.warning('请选择要应用的班级');
      return;
    }

    try {
      // 创建用户模板
      const response = await pointsTemplatesAPI.createTemplate({
        template_name: currentTemplate.template_name + ' (我的)',
        description: currentTemplate.description,
        school_stage: currentTemplate.school_stage,
        based_on_system_template_id: currentTemplate.id,
        selected_items: selectedItems,
        class_id: selectedClassId
      });

      if (response.data.success) {
        message.success('模板已应用到班级！');
        setDetailVisible(false);
        onSelectTemplate && onSelectTemplate(response.data.data);
      }
    } catch (error) {
      console.error('应用模板失败:', error);
      message.error('应用模板失败');
    }
  };

  // 渲染项目列表
  const renderItems = (items = [], editable = false) => {
    const safeItems = Array.isArray(items) ? items : [];
    // 按选中的分类过滤
    let filteredItems = selectedCategory 
      ? safeItems.filter(item => item.category === selectedCategory)
      : safeItems;
    
    let addItems = filteredItems.filter(item => item.item_type === 'add');
    let deductItems = filteredItems.filter(item => item.item_type === 'deduct');

    // 排序
    const sortItems = (itemsList) => {
      if (sortBy === 'name') {
        return [...itemsList].sort((a, b) => a.item_name.localeCompare(b.item_name, 'zh-CN'));
      } else if (sortBy === 'points') {
        return [...itemsList].sort((a, b) => b.default_points - a.default_points);
      }
      return itemsList;
    };

    addItems = sortItems(addItems);
    deductItems = sortItems(deductItems);

    const renderItemTag = (item) => {
      const isEditingThisItem = editingPoints[item.id];
      
      return (
        <div key={item.id} style={{ display: 'inline-block', marginBottom: 10, marginRight: 10 }}>
          <Tag 
            color={item.item_type === 'add' ? 'green' : 'red'} 
            closable={editable}
            onClose={() => handleRemoveItem(item.id)}
            style={{ 
              marginBottom: 0, 
              padding: '6px 12px',
              fontSize: 14,
              lineHeight: '20px'
            }}
          >
            <Space size={6}>
              <span style={{ fontSize: 14 }}>{item.item_name}</span>
              {editable && !isEditingThisItem ? (
                <span 
                  onClick={() => setEditingPoints({...editingPoints, [item.id]: true})}
                  style={{ 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    fontSize: 14,
                    fontWeight: 'bold',
                    padding: '2px 4px'
                  }}
                >
                  {item.item_type === 'add' ? '+' : ''}{item.default_points}分
                </span>
              ) : editable && isEditingThisItem ? (
                <InputNumber
                  size="middle"
                  min={0.1}
                  max={50}
                  step={0.1}
                  precision={1}
                  value={item.default_points}
                  onChange={(val) => handleChangePoints(item.id, val)}
                  onBlur={() => setEditingPoints({...editingPoints, [item.id]: false})}
                  onPressEnter={() => setEditingPoints({...editingPoints, [item.id]: false})}
                  style={{ width: 70, fontSize: 14 }}
                  autoFocus
                />
              ) : (
                <span style={{ fontSize: 14, fontWeight: 'bold' }}>
                  {item.item_type === 'add' ? '+' : ''}{item.default_points}分
                </span>
              )}
              {item.isNew && <span style={{ fontSize: 11, opacity: 0.7 }}>(新)</span>}
              {item.isModified && <span style={{ fontSize: 11, opacity: 0.7 }}>(改)</span>}
              {item.isCustom && <span style={{ fontSize: 11, opacity: 0.7 }}>(自定义)</span>}
            </Space>
          </Tag>
        </div>
      );
    };

    return (
      <div>
        {/* 分类和排序选项 */}
        {items.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={16}>
                <Space size="large">
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>📋 选择分类：</span>
                  {categories.map(cat => (
                    <Button
                      key={cat}
                      type={selectedCategory === cat ? 'primary' : 'default'}
                      size="middle"
                      onClick={() => setSelectedCategory(cat)}
                      style={{
                        borderRadius: 6,
                        fontWeight: selectedCategory === cat ? 'bold' : 'normal'
                      }}
                    >
                      {cat}
                    </Button>
                  ))}
                </Space>
              </Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <Space>
                  <span style={{ fontSize: 13, color: '#666' }}>排序：</span>
                  <Select 
                    size="small" 
                    value={sortBy} 
                    onChange={setSortBy}
                    style={{ width: 120 }}
                  >
                    <Option value="default">默认顺序</Option>
                    <Option value="name">按名称</Option>
                    <Option value="points">按分值</Option>
                  </Select>
                </Space>
              </Col>
            </Row>
          </div>
        )}
        
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 16, margin: 0 }}>
                <PlusOutlined style={{ color: '#52c41a' }} /> 加分项 ({addItems.length})
                {selectedCategory && <span style={{ fontSize: 13, fontWeight: 'normal', color: '#999', marginLeft: 8 }}>当前分类</span>}
              </h4>
              {editable && (
                <Space>
                  <Button 
                    size="small" 
                    type="dashed" 
                    icon={<PlusOutlined />}
                    onClick={() => setSelectorVisible(true)}
                  >
                    从池中添加
                  </Button>
                  <Button 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={handleAddCustomItem}
                  >
                    自定义
                  </Button>
                </Space>
              )}
            </div>
            <div style={{ 
              maxHeight: 450, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '8px 0'
            }}>
              {addItems.map(renderItemTag)}
              {addItems.length === 0 && <Empty description="暂无加分项" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 16, margin: 0 }}>
                <MinusOutlined style={{ color: '#f5222d' }} /> 扣分项 ({deductItems.length})
              </h4>
              {editable && (
                <Space>
                  <Button 
                    size="small" 
                    type="dashed" 
                    icon={<PlusOutlined />}
                    onClick={() => setSelectorVisible(true)}
                  >
                    从池中添加
                  </Button>
                  <Button 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={handleAddCustomItem}
                  >
                    自定义
                  </Button>
                </Space>
              )}
            </div>
            <div style={{ 
              maxHeight: 450, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '8px 0'
            }}>
              {deductItems.map(renderItemTag)}
              {deductItems.length === 0 && <Empty description="暂无扣分项" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      <Spin spinning={loading}>
        {templates.length > 0 ? (
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3 }}
            dataSource={templates}
            renderItem={(template) => (
              <List.Item>
                <Card
                  size="small"
                  title={template.template_name}
                  extra={<Tag color="blue">{template.school_stage}</Tag>}
                >
                  <p style={{ minHeight: 40, fontSize: 12 }}>{template.description}</p>
                  <div style={{ marginBottom: 8 }}>
                    <Tag>{template.item_count || 0} 个项目</Tag>
                  </div>
                  <div style={{ marginTop: 8, textAlign: 'right' }}>
                    <Space>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(template)}
                      >
                        查看详情
                      </Button>
                    </Space>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        ) : (
          <Empty description="暂无系统模板" />
        )}
      </Spin>

      {/* 模板详情弹窗 */}
      <Modal
        title={currentTemplate?.template_name}
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setIsEditing(false);
        }}
        width="90%"
        style={{ top: 20, maxWidth: 1400 }}
        bodyStyle={{ 
          maxHeight: 'calc(100vh - 200px)', 
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={() => setDetailVisible(false)}>
            取消
          </Button>,
          !isEditing ? (
            <Button 
              key="edit" 
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
            >
              编辑模板
            </Button>
          ) : (
            <Button 
              key="done" 
              type="default"
              onClick={() => setIsEditing(false)}
            >
              完成编辑
            </Button>
          ),
          <div key="apply" style={{ display: 'inline-block', marginLeft: 8 }}>
            <Select
              placeholder="选择班级"
              style={{ width: 150, marginRight: 8 }}
              value={selectedClassId}
              onChange={setSelectedClassId}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>{cls.name}</Option>
              ))}
            </Select>
            <Button 
              type="primary" 
              icon={<CheckOutlined />}
              onClick={handleApplyTemplate}
            >
              应用到班级
            </Button>
          </div>
        ]}
      >
        {templateDetail && (
          <div>
            <p style={{ color: '#666', marginBottom: 16 }}>{currentTemplate.description}</p>
            <Divider />
            {renderItems(templateDetail.items, isEditing)}
            <Divider />
            <p style={{ color: '#999', fontSize: 12 }}>
              提示：{isEditing ? '您可以添加或删除项目，完成后点击"应用到班级"' : '点击"编辑模板"可以自定义项目'}
            </p>
          </div>
        )}
      </Modal>

      {/* 项目选择器 */}
      <PointsItemSelector
        visible={selectorVisible}
        onClose={() => setSelectorVisible(false)}
        onSelect={handleAddFromPool}
      />

      {/* 自定义项目对话框 */}
      <Modal
        title="自定义积分项目"
        open={customModalVisible}
        onCancel={() => setCustomModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={customForm}
          layout="vertical"
          onFinish={handleSaveCustomItem}
        >
          <Form.Item
            name="item_name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="例如：考试进步20名" />
          </Form.Item>

          <Form.Item
            name="item_type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="选择加分或扣分">
              <Option value="add">加分</Option>
              <Option value="deduct">扣分</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请输入分类' }]}
          >
            <Input placeholder="例如：学习表现" />
          </Form.Item>

          <Form.Item
            name="default_points"
            label="分值"
            rules={[{ required: true, message: '请输入分值' }]}
          >
            <InputNumber 
              min={0.1} 
              max={50} 
              step={0.1}
              precision={1}
              style={{ width: '100%' }} 
              placeholder="1-50分"
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setCustomModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SystemTemplates;

